import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { Base, ReverLoading } from '@itsrever/design-system'
import {
  useGroupRouting,
  useGroupWarehouses
} from '@/application/manage-warehouses'
import { captureWarehousesEvent } from '@/infrastructure/posthog'
import { LoadError, PageHeader } from '@/ui/shared'
import { RoutingRules } from './routing-rules'
import { WarehouseList } from './warehouse-list'
import { WarehousesScope } from '@/hooks/use-warehouses-scope'

const { Badge } = Base

export type WarehousesTab = 'list' | 'rules'

interface WarehousesProps {
  scope: Exclude<WarehousesScope, { kind: 'none' }>
  tab: WarehousesTab
  onShowGroup: () => void
  onShowStore: (ecommerceId: string) => void
}

export const Warehouses: React.FC<WarehousesProps> = ({
  scope,
  tab,
  onShowGroup,
  onShowStore
}) => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { warehouses, loadingWarehouses, warehousesError, reloadWarehouses } =
    useGroupWarehouses(scope.group)
  const { routing, loadingRouting, routingError, reloadRouting } =
    useGroupRouting(scope.group)

  const loaded =
    !loadingWarehouses && !loadingRouting && !warehousesError && !routingError
  const ecommerceId = scope.kind === 'store' ? scope.ecommerceId : undefined
  const viewKey = [tab, scope.group, scope.kind, ecommerceId].join('|')
  const capturedView = useRef<string>()
  // Once per tab and scope: a failed refetch and its retry are the same view, and the counts are
  // only context for it.
  useEffect(() => {
    if (!loaded || capturedView.current === viewKey) return
    capturedView.current = viewKey
    captureWarehousesEvent('Warehouses viewed', {
      group: scope.group,
      tab,
      level: scope.kind,
      ecommerceId,
      storeCount: scope.stores.length,
      warehouseCount: warehouses.length,
      ruleCount: routing.rules.length,
      storeOverrideCount: Object.keys(routing.overrides).length
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, viewKey])

  const showRules = () =>
    navigate(`/configuration/warehouses/rules${location.search}`)

  const header = (
    <PageHeader
      title={t(tab === 'rules' ? 'warehouses.tabs.rules' : 'warehouses.title')}
      description={
        tab === 'rules'
          ? t('warehouses.rules.description')
          : scope.kind === 'group'
            ? t('warehouses.description_group', { group: scope.group })
            : t('warehouses.description_store', { store: scope.ecommerceId })
      }
      actions={
        scope.kind === 'store' ? (
          <Badge tone="info">
            {t('warehouses.scope_store', { store: scope.ecommerceId })}
          </Badge>
        ) : undefined
      }
    />
  )

  if (loadingWarehouses || loadingRouting) return <ReverLoading />
  // A failed load must not read as an empty group: it would invite re-creating every warehouse.
  if (warehousesError || routingError) {
    return (
      <>
        {header}
        <div className="px-7 py-6">
          <LoadError
            message={t('warehouses.load_error')}
            onRetry={() => {
              reloadWarehouses()
              reloadRouting()
            }}
          />
        </div>
      </>
    )
  }

  return (
    <>
      {header}
      <div className="flex max-w-screen-xl flex-col gap-4 px-7 py-4">
        {tab === 'list' ? (
          <WarehouseList
            // A scope switch starts over: the search and the selection belong to the previous one.
            key={scope.kind === 'store' ? scope.ecommerceId : scope.group}
            scope={scope}
            warehouses={warehouses}
            routing={routing}
            onShowGroup={onShowGroup}
            onShowRules={showRules}
          />
        ) : (
          <RoutingRules
            scope={scope}
            warehouses={warehouses}
            routing={routing}
            onShowGroup={onShowGroup}
            onShowStore={onShowStore}
          />
        )}
      </div>
    </>
  )
}
